import React from 'react';
import { TableRow, TableCell, Skeleton } from '@mui/material';

interface Props {
  /** Number of columns in the host table — one skeleton bar per cell. */
  columns: number;
  /** How many placeholder rows to render. Default 6 matches the Enrollments
   *  page density; use smaller numbers for compact tables. */
  rows?: number;
  /** Fixed row height so the layout doesn't jump when the real rows swap in. */
  rowHeight?: number;
}

/**
 * Fixed-shape skeleton rows for a MUI Table. Keeps the surrounding filter
 * bar and header row rendered so a filter / search change feels like the
 * rows refreshing, not the whole page reloading.
 *
 * Usage: render these INSIDE a <TableBody> when `loading && rows.length === 0`.
 * On subsequent re-fetches with existing rows, leave the rows mounted and
 * optionally overlay a <LinearProgress /> on the table container — the
 * header + filters never flicker.
 */
const TableRowSkeletons: React.FC<Props> = ({ columns, rows = 6, rowHeight = 48 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={`sk-${r}`} sx={{ height: rowHeight }}>
        {Array.from({ length: columns }).map((__, c) => (
          <TableCell key={c}>
            <Skeleton variant="text" width={c === 0 ? '70%' : '50%'} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export default TableRowSkeletons;
